import { SETTING_TYPE, type Setting } from '@circuitry-ai/doc-data';
import { azureSupportedLanguages } from './constants';
import {
  type AdvisorRegionSetting,
  ISO_COUNTRIES,
  ISO_LANGUAGES,
  normalizeAdvisorRegions,
} from './isoRegionConfig';

const AZURE_LANGUAGE_CODES = Object.keys(azureSupportedLanguages);
const DEFAULT_COUNTRY_CODE = 'US';
const DEFAULT_AZURE_LANGUAGE = 'en-US';

export const DEFAULT_FALLBACK_REGION: AdvisorRegionSetting = {
  country_code: DEFAULT_COUNTRY_CODE,
  country_name: 'United States',
  languages: [{ code: 'en', name: 'English' }],
};

export type CountryConverseState = {
  enableCountry: boolean;
  regions: AdvisorRegionSetting[];
};

export function getCountryConverseState(
  advisorAbilities: Record<string, Setting> | undefined | null
): CountryConverseState {
  const countryAbility = advisorAbilities?.[SETTING_TYPE.COUNTRY];
  const enableCountry = Boolean(countryAbility?.is_active);
  const regions = normalizeAdvisorRegions(countryAbility?.metadata?.regions);
  return { enableCountry, regions };
}

/** Regions for converse UI; falls back to United States + English when none configured. */
export function getConversationRegions(
  regions: AdvisorRegionSetting[]
): AdvisorRegionSetting[] {
  return regions.length > 0 ? regions : [DEFAULT_FALLBACK_REGION];
}

export function buildCountryOptionsMap(
  regions: AdvisorRegionSetting[]
): Record<string, string> {
  return getConversationRegions(regions).reduce<Record<string, string>>((acc, region) => {
    acc[region.country_code] = region.country_name;
    return acc;
  }, {});
}

export function isoLanguageToAzureCode(
  isoCode: string,
  countryCode?: string
): string {
  const lang = isoCode.toLowerCase().split('-')[0];
  if (countryCode) {
    const preferred = `${lang}-${countryCode.toUpperCase()}`;
    const exact = AZURE_LANGUAGE_CODES.find(
      (code) => code.toLowerCase() === preferred.toLowerCase()
    );
    if (exact) return exact;
  }

  const prefixMatch = AZURE_LANGUAGE_CODES.find((code) =>
    code.toLowerCase().startsWith(`${lang}-`)
  );
  if (prefixMatch) return prefixMatch;

  if (lang === 'en') return DEFAULT_AZURE_LANGUAGE;
  return DEFAULT_AZURE_LANGUAGE;
}

export function buildLanguageOptionsForRegion(
  region: AdvisorRegionSetting | undefined
): Record<string, string> {
  if (!region) return { [DEFAULT_AZURE_LANGUAGE]: 'English' };
  const map: Record<string, string> = {};
  for (const lang of region.languages) {
    const azureCode = isoLanguageToAzureCode(lang.code, region.country_code);
    map[azureCode] = lang.name;
  }
  return Object.keys(map).length > 0
    ? map
    : { [DEFAULT_AZURE_LANGUAGE]: 'English' };
}

function getBrowserLocale(): { country: string; language: string } {
  const langTag = typeof navigator !== 'undefined' ? navigator.language : 'en-US';
  const parts = langTag.split('-');
  const language = (parts[0] ?? 'en').toLowerCase();
  let country = parts[1]?.toUpperCase();

  try {
    const locale = new Intl.Locale(langTag);
    country = locale.region ?? country;
  } catch {
    // ignore unsupported locale tags
  }

  return { country: country ?? DEFAULT_COUNTRY_CODE, language };
}

export function resolveDefaultCountryAndLanguage(
  regions: AdvisorRegionSetting[]
): { countryCode: string; languageAzureCode: string } {
  const fallback = {
    countryCode: DEFAULT_COUNTRY_CODE,
    languageAzureCode: DEFAULT_AZURE_LANGUAGE,
  };
  if (!regions.length) return fallback;

  const browser = getBrowserLocale();
  const browserCountryRegion = regions.find(
    (region) => region.country_code === browser.country
  );
  const usRegion = regions.find(
    (region) => region.country_code === DEFAULT_COUNTRY_CODE
  );
  const region = browserCountryRegion ?? usRegion ?? regions[0];

  const browserLanguage = region.languages.find(
    (lang) => lang.code.toLowerCase() === browser.language
  );
  const englishLanguage = region.languages.find(
    (lang) => lang.code.toLowerCase() === 'en'
  );
  const isoLang =
    browserLanguage?.code ??
    englishLanguage?.code ??
    region.languages[0]?.code ??
    'en';

  return {
    countryCode: region.country_code,
    languageAzureCode: isoLanguageToAzureCode(isoLang, region.country_code),
  };
}

export function resolveLanguageSelection(
  currentKey: string,
  availableLanguages: Record<string, string>,
  preferredKey = DEFAULT_AZURE_LANGUAGE
): string {
  const keys = Object.keys(availableLanguages);
  if (!keys.length) return preferredKey;
  if (keys.includes(currentKey)) return currentKey;
  if (keys.includes(preferredKey)) return preferredKey;
  const englishKey =
    keys.find((key) => key.toLowerCase() === preferredKey.toLowerCase()) ??
    keys.find((key) => key.toLowerCase().startsWith('en-'));
  return englishKey ?? keys[0];
}

export function buildConversationRegionSelection(
  countryCode: string,
  languageAzureCode: string,
  countryOptionsMap: Record<string, string>,
  languageOptionsMap: Record<string, string>
) {
  return {
    countryCode: countryCode.toUpperCase(),
    countryName: countryOptionsMap[countryCode] ?? countryCode,
    languageCode: languageAzureCode.split('-')[0]?.toLowerCase() ?? 'en',
    languageName: languageOptionsMap[languageAzureCode] ?? languageAzureCode,
    languageLocale: languageAzureCode,
  };
}

export function getTenantCountryRegionsFromSettings(
  settings: Array<{ ability?: string; metadata?: { regions?: unknown } }> | undefined | null
): AdvisorRegionSetting[] {
  if (!Array.isArray(settings)) return [];
  const countrySetting = settings.find((entry) => entry.ability === SETTING_TYPE.COUNTRY);
  return normalizeAdvisorRegions(countrySetting?.metadata?.regions);
}

export function getTenantTranslationLanguagesFromSettings(
  settings: Array<{ ability?: string; metadata?: { selected_languages?: string[] } }> | undefined | null
): string[] {
  if (!Array.isArray(settings)) return [];
  const translationSetting = settings.find((entry) => entry.ability === SETTING_TYPE.TRANSLATION);
  const languages = translationSetting?.metadata?.selected_languages;
  if (!Array.isArray(languages)) return [];
  return languages.filter((language): language is string => typeof language === 'string' && language.length > 0);
}

export function getTenantTranslationLanguagesFromAbilitiesPayload(
  advisorAbilitiesData: Array<{ tenant_abilities?: Array<{ ability?: string; metadata?: { selected_languages?: string[] } }> }> | undefined | null
): string[] {
  const tenantAbilities = advisorAbilitiesData?.[0]?.tenant_abilities;
  return getTenantTranslationLanguagesFromSettings(tenantAbilities);
}

export function getTenantCountryRegionsFromAbilitiesPayload(
  advisorAbilitiesData: Array<{ tenant_abilities?: Array<{ ability?: string; metadata?: { regions?: unknown } }> }> | undefined | null
): AdvisorRegionSetting[] {
  const tenantAbilities = advisorAbilitiesData?.[0]?.tenant_abilities;
  return getTenantCountryRegionsFromSettings(tenantAbilities);
}

export function filterIsoCountriesByRegions(
  regions: AdvisorRegionSetting[]
): typeof ISO_COUNTRIES {
  if (!regions.length) return [];
  const codes = new Set(regions.map((region) => region.country_code.toUpperCase()));
  return ISO_COUNTRIES.filter((country) => codes.has(country.code));
}

export function filterIsoLanguagesByRegion(
  regions: AdvisorRegionSetting[],
  countryCode: string
): typeof ISO_LANGUAGES {
  const region = regions.find(
    (entry) => entry.country_code.toUpperCase() === countryCode.toUpperCase()
  );
  if (!region) return [];
  const codes = new Set(region.languages.map((language) => language.code.toLowerCase()));
  return ISO_LANGUAGES.filter((language) => codes.has(language.code.toLowerCase()));
}
